import uuid from 'uuid/v4';
import apiCall from 'shared/utils/apiCall';
import apolloClient from 'store/apolloClient';
import errorAction from 'shared/utils/errorAction';
import createAsyncAction from 'shared/utils/createAsyncAction';
import { replaceTo } from 'modules/auth/actions/redirectAction';
import { getUserFullNameWithMiddleName } from 'modules/user/utils/user';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { stopConsultation } from 'modules/conference/actions/stopConsultation';
import { createConversation } from 'modules/conference/actions/_newConversationActions/createConversation';
import {
  getConversationCustomer,
  isCurrentConversationSIP,
  isCurrentConversationMCU,
} from 'modules/appointmentInfo/selectors/currentAppointment';

import createAction from './';
import * as services from '../services/generateCallJoinParams';
import { CONSULTATION_DID_INIT } from '../constants/consultationActionTypes';

import {
  initProvider,
  startCall,
  stopCall,
} from './videoConsultation';

import {
  onMessage,
  upstreamStateChange,
  downstreamStateChange,
  onMcuVideoLayoutChange,
  downstreamStateChangeSIP,
} from './videoConsultationLifecircle';

import {
  handlePatientCouldNotConnect,
  handleParticipantHasNotPermission,
} from './startConsultationHandlers';


export const SAVE_LOCAL_VIDEO = createAction('SAVE_LOCAL_VIDEO');
export const CONSULTATION_PARAMS = createAction('CONSULTATION_PARAMS');
export const INITIATE_CONSULTATION = createAction('INITIATE_CONSULTATION');
export const START_CONSULTATION = createAsyncAction(createAction('START_CONSULTATION'));
export const GENERATE_CALL_JOIN_PARAMS = createAsyncAction(createAction('GENERATE_CALL_JOIN_PARAMS'));

const getConsultationCallbacs = (isSIPCall, dispatch) => {
  return isSIPCall
    ? {
      observeOnUpstreamStateChange: (payload) => dispatch(upstreamStateChange(payload)),
      observeOnDownstreamStateChange: (payload) => dispatch(downstreamStateChangeSIP(payload)),
      observeOnRemoteClientLeave: () => dispatch(stopConsultation()),
    }
    : {
      observeOnMessage: (client, payload) => dispatch(onMessage(client, payload)),
      observeOnUpstreamStateChange: (payload) => dispatch(upstreamStateChange(payload)),
      observeOnDownstreamStateChange: (payload) => dispatch(downstreamStateChange(payload)),
      onMcuVideoLayoutChange: (payload) => dispatch(onMcuVideoLayoutChange(payload)),
    };
};

const handleStartCallErrors = (err) => (dispatch) => {
  if (err.name === 'PermissionDeniedError') {
    return dispatch(handleParticipantHasNotPermission());
  }

  return dispatch(handlePatientCouldNotConnect());
};

export const startConsultation = () => (dispatch, getState) => {
  const isReactNative = /RnUmmanu/.test(navigator.userAgent);
  if (!isReactNative) replaceTo('/appointments');

  const state = getState();

  if (state.common.browserMediaPermissions.localMediaPermission === requestsStatuses.failure) {
    throw new Error('No local media permission');
  }

  const { conversationId } = state.conference.currentConversation;
  const { phoneNumber } = getConversationCustomer(state);
  const isSIPCall = isCurrentConversationSIP(state);

  dispatch({ type: START_CONSULTATION.pending });

  const deviceId = state.conference.consultationParams.deviceId || uuid();
  const instanceId = state.conference.consultationParams.instanceId || uuid();

  dispatch({
    type: CONSULTATION_PARAMS,
    payload: { instanceId, deviceId },
  });

  const apiCallParams = {
    mutation: services.GENERATE_CALL_JOIN_PARAMS,
    variables: { conversationId, deviceId, instanceId },
    fetchPolicy: 'no-cache',
  };

  dispatch({ type: GENERATE_CALL_JOIN_PARAMS.pending });

  return apiCall(apolloClient.mutate, apiCallParams)
    .then(({ data }) => {
      const { deviceId, instanceId, jwtRegister, userId, applicationId, mediaGatewayUrl, mode } = data.generateCallJoinParams;
      const name = getUserFullNameWithMiddleName(state.auth.userInfo.profile);

      dispatch({
        type: GENERATE_CALL_JOIN_PARAMS.success,
        payload: { data },
      });

      dispatch({
        type: CONSULTATION_PARAMS,
        payload: { mode },
      });

      const options = {
        name,
        userId,
        deviceId,
        phoneNumber,
        applicationId,
        clientId: instanceId,
        gatewayUrl: mediaGatewayUrl,
        isMCU: isCurrentConversationMCU(state),
        volume: state.conference.consultationInfo.outputSoundLevel,
        getIsVideoMuted: () => getState().conference.consultationInfo.selfVideoOff,
        getIsAudioMuted: () => getState().conference.consultationInfo.selfAudioOff,
        observeCallbacks: getConsultationCallbacs(isSIPCall, dispatch),
      };

      return dispatch(initProvider(options))
        .then((videoProvider) => {
          window.videoProvider = videoProvider;
          dispatch({ type: START_CONSULTATION.success });

          return dispatch(startCall(jwtRegister));
        })
        .then(() => {
          dispatch({
            type: SAVE_LOCAL_VIDEO,
            payload: { localVideo: true },
          });

          dispatch({ type: CONSULTATION_DID_INIT });
        });
    })
    .catch((err = {}) => {
      dispatch(stopCall());
      dispatch(handleStartCallErrors(err));
      dispatch(errorAction(START_CONSULTATION.failure, err));
    });
};

export const joinToConsultation = () => dispatch => {
  dispatch({ type: INITIATE_CONSULTATION });
  dispatch(startConsultation());
};

export const restoreConsultation = () => dispatch => {
  return dispatch(startConsultation());
};
